<template>
  <div class="reviewer-page">
    <Header />

    <!-- Main Container for all content below header -->
    <div class="main-container">
      <!-- Loading State -->
      <div v-if="loading" class="viva-section">
        <div class="viva-spinner-section">
          <i class="fa fa-spinner fa-spin viva-spinner"></i>
          <p class="viva-spinner-text">Loading student review data...</p>
        </div>
      </div>

      <!-- Error State -->
      <div v-if="error" class="viva-section">
        <div class="viva-alert viva-alert-danger">
          <h4>Review Load Error</h4>
          <p>{{ error }}</p>
          <div class="error-actions">
            <button class="viva-btn viva-btn-orange" @click="loadStudentData">
              Try Again
            </button>
          </div>
        </div>
      </div>

      <!-- Main Content (when loaded) -->
      <div v-show="!loading && !error" class="reviewer-interface">
        <!-- Student Name Header -->
        <div class="student-name-header">
          <h1 v-if="!canLinkToStudentProgress">{{ studentData?.name }}</h1>
          <h1 v-else>
            <VivaTooltip text="See this student's other VIVAs and Highlight Reel">
              <router-link
                :to="{ name: 'student-progress', params: { student_id: studentData.id }, query: { course_id: currentViva.course_id } }"
              >{{ studentData?.name }}</router-link>
            </VivaTooltip>
          </h1>
        </div>

        <!-- VIVA Content with Tabs -->
        <div v-if="vivas.length > 0" class="viva-content-wrapper">
          <!-- VIVA Tabs (only show when multiple VIVAs) -->
          <div v-if="vivas.length > 1" class="viva-tabs">
              <button
                v-for="(viva, index) in vivas"
                :key="viva.send_id"
                :class="['viva-tab', { active: activeVivaIndex === index }]"
                @click="switchViva(index)"
              >
                VIVA #{{ viva.viva_number }}
              </button>
          </div>

          <!-- All VIVA Content (rendered but toggled with v-show) -->
          <div
            v-for="(viva, vivaIndex) in vivas"
            :key="viva.send_id"
            v-show="activeVivaIndex === vivaIndex"
            :class="['viva-content', { 'with-tabs': vivas.length > 1 }]"
          >
            <!-- Project Title -->
            <div class="project-title">
              <h2>{{ viva.paper_title || 'Untitled Project' }}</h2>
            </div>

            <div
              ref="projectContent"
              class="project-content"
              :style="{ '--content-total-height': (contentTotalHeight + 'px') }"
            >
              <!-- PDF display collumn -->
              <div
                class="paper-panel panel-width"
                v-show="showPDF"
                v-if="currentProjectUrl && PDFRendered && viva.upload_type === 'pdf'"
              >
                <div class="paper-viewer-container">
                  <button
                    class="viva-btn viva-btn-green submit-button submit-button-small paper-close"
                    title="Hide Project"
                    @click.prevent="togglePDFViewer"
                  >
                    <i class="fa fa-times"></i>
                  </button>
                  <div class="paper-viewer">
                    <div class="paper-scroll-area">
                      <PDFViewer
                        :pdfUrl="currentProjectUrl"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Takes Accordion (only show for admissions with multiple takes) -->
              <div
                v-if="viva.takes.length > 1 && viva.viva_exam.viva_type === 'admissions'"
                class="takes-accordion"
                :style="{ 'flex-basis': `${showPDF ? '5' : '10'}0%`}"
              >
                <div
                  v-for="take in viva.takes"
                  :key="take.id"
                  class="take-item"
                  :class="{ expanded: expandedTakeId === take.id }"
                >
                  <!-- Take Header (clickable) -->
                  <div class="take-header" @click="toggleTake(take.id)">
                    <span class="take-number">Take #{{ take.take_number }}</span>
                    <span class="take-date">Recorded {{ formatDate(take.recorded_date) }}</span>
                    <i :class="['fa', 'take-chevron', expandedTakeId === take.id ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
                  </div>

                  <!-- Take Content (expandable) -->
                  <div v-show="expandedTakeId === take.id" class="take-content">
                    <div class="reviewer-layout" :style="{ 'flex-direction': showPDF ? 'column' : ''}">
                      <!-- Left Column: Video Player -->
                      <div class="student-panel">
                        <div class="student-card">
                          <!-- Video Player Area -->
                          <div class="video-container">
                            <!-- Video player - render all but only show active -->
                            <IVPlayer
                              v-if="take.uid"
                              v-show="expandedTakeId === take.id && activeVivaIndex === vivaIndex"
                              ref="playbackVideo"
                              preload="metadata"
                              :videoSrc="getVideoUrl(take)"
                              :questions="getQuestionsForTake(take)"
                              :showMarkerTips="false"
                              :customid="'review_video_viva' + vivaIndex + '_take' + take.id"
                              @player-ready="handlePlayerReady"
                            />

                            <!-- No video placeholder -->
                            <div v-if="!take.uid" class="no-video-placeholder">
                              <i class="fa fa-video-camera"></i>
                              <p>No recording found</p>
                            </div>
                          </div>

                          <!-- Submit Project Button -->
                          <div class="submit-row">
                            <button
                              v-if="viva.upload_type === 'pdf'"
                              class="viva-btn viva-btn-green submit-button"
                              @click.prevent="togglePDFViewer"
                            >
                              <span>{{ showPDF ? 'HIDE' : 'VIEW' }} PROJECT</span>
                            </button>
                            <button
                              class="viva-btn viva-btn-green submit-button"
                              :class="viva.upload_type !== 'pdf' ? '' : 'submit-button-small'"
                              @click="downloadStudentProject"
                              :disabled="downloadingProject"
                              :title="viva.upload_type !== 'pdf' ? '' : 'Download Project'"
                            >
                              <i v-if="downloadingProject" class="fa fa-spinner fa-spin"></i>
                              <i v-else-if="viva.upload_type === 'pdf'" class="fa fa-download"></i>
                              <span v-else>DOWNLOAD PROJECT</span>
                            </button>
                          </div>

                          <!-- Transcript Link -->
                          <div v-if="take.transcription && showTranscriptions && expandedTakeId === take.id" class="transcript-link-container">
                            <a href="#" @click.prevent="showTranscript = !showTranscript" class="transcript-link">
                              <i class="fa fa-file-text-o"></i>
                              {{ showTranscript ? 'Hide Transcript' : 'View Transcript' }}
                            </a>
                          </div>

                          <!-- Transcript Display -->
                          <div v-if="showTranscript && take.transcription && showTranscriptions" class="transcript-container">
                            <div class="transcript-disclaimer">AI generated and may contain mistakes</div>
                            <div class="transcript-display">
                              <div class="transcript-content">{{ take.transcription }}</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Right Column: Questions -->
                      <div class="questions-panel">
                        <div class="questions-section">
                          <div
                            class="question-item"
                            v-for="(question, qIndex) in take.questions"
                            :key="question.id"
                          >
                            <div
                              class="question-display-wrapper"
                              @click="seekToTimestamp(question.timestamp)"
                            >
                              <div v-if="question.suggested && viva.viva_exam?.viva_type === 'assignment'" class="suggested-reel-indicator" @click.stop>
                                <VivaTooltip text="Based on our metrics, this is the top response given by this student in this VIVA video">
                                  <img src="/viva/suggested-reel-stamp.svg" alt="Suggested for Highlight Reel" />
                                </VivaTooltip>
                              </div>
                              <div class="question-indicator">
                                <span class="question-number">{{ qIndex + 1 }}</span>
                              </div>
                              <div class="question-display">
                                {{ question.question || `QUESTION ${qIndex + 1}` }}
                                <span v-if="question.timestamp" class="timestamp-badge">{{
                                  question.timestamp
                                }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Single Take Layout (assignments, or admissions with single take) -->
              <div v-else class="single-take-layout" :class="{ 'panel-width': showPDF }">
                <div class="reviewer-layout" :style="{ 'flex-direction': showPDF ? 'column' : ''}">
                  <!-- Video Player Panel -->
                  <div class="student-panel" :class="{ 'panel-width': !showPDF }">
                    <div class="student-card">
                      <div class="video-container">
                        <IVPlayer
                          v-show="viva.takes[0]?.uid && activeVivaIndex === vivaIndex"
                          ref="playbackVideo"
                          preload="metadata"
                          :videoSrc="getVideoUrl(viva.takes[0])"
                          :questions="getQuestionsForTake(viva.takes[0])"
                          :showMarkerTips="false"
                          :customid="'review_video_viva' + vivaIndex"
                          @player-ready="handlePlayerReady"
                        />
                        <div v-if="!viva.takes[0]?.uid" class="no-video-placeholder">
                          <i class="fa fa-video-camera"></i>
                          <p>No recording found</p>
                        </div>
                      </div>

                      <!-- Submit Project Button -->
                      <div class="submit-row">
                        <button
                          v-if="viva.upload_type === 'pdf'"
                          class="viva-btn viva-btn-green submit-button"
                          @click.prevent="togglePDFViewer"
                        >
                          <span>{{ showPDF ? 'HIDE' : 'VIEW' }} PROJECT</span>
                        </button>
                        <button
                          class="viva-btn viva-btn-green submit-button"
                          :class="viva.upload_type !== 'pdf' ? '' : 'submit-button-small'"
                          @click="downloadStudentProject"
                          :disabled="downloadingProject"
                          :title="viva.upload_type !== 'pdf' ? '' : 'Download Project'"
                        >
                          <i v-if="downloadingProject" class="fa fa-spinner fa-spin"></i>
                          <i v-else-if="viva.upload_type === 'pdf'" class="fa fa-download"></i>
                          <span v-else>DOWNLOAD PROJECT</span>
                        </button>
                      </div>

                      <div v-if="viva.takes[0]?.transcription && showTranscriptions" class="transcript-link-container">
                        <a href="#" @click.prevent="showTranscript = !showTranscript" class="transcript-link">
                          <i class="fa fa-file-text-o"></i>
                          {{ showTranscript ? 'Hide Transcript' : 'View Transcript' }}
                        </a>
                      </div>
                      <div v-if="showTranscript && viva.takes[0]?.transcription && showTranscriptions" class="transcript-container">
                        <div class="transcript-disclaimer">AI generated and may contain mistakes</div>
                        <div class="transcript-display">
                          <div class="transcript-content">{{ viva.takes[0].transcription }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- Questions Panel -->
                  <div class="questions-panel" :class="{ 'panel-width': !showPDF }">
                    <div class="questions-section">
                      <div
                        class="question-item"
                        v-for="(question, qIndex) in viva.takes[0]?.questions || []"
                        :key="question.id"
                      >
                        <div class="question-display-wrapper" @click="seekToTimestamp(question.timestamp)">
                          <div v-if="question.suggested && viva.viva_exam?.viva_type === 'assignment'" class="suggested-reel-indicator" @click.stop>
                            <VivaTooltip text="Based on our metrics, this is the top response given by this student in this VIVA video">
                              <img src="/viva/suggested-reel-stamp.svg" alt="Suggested for Highlight Reel" />
                            </VivaTooltip>
                          </div>
                          <div class="question-indicator">
                            <span class="question-number">{{ qIndex + 1 }}</span>
                          </div>
                          <div v-if="question.highlighted" class="highlight-reel-indicator">
                            <img src="/viva/highlight-reel-stamp.svg" alt="Highlight Reel" />
                          </div>
                          <div class="question-display" :class="{ 'question-display--highlighted': question.highlighted }">
                            {{ question.question || `QUESTION ${qIndex + 1}` }}
                            <span v-if="question.timestamp" class="timestamp-badge">{{ question.timestamp }}</span>
                          </div>
                        </div>
                        <!-- Highlight Reel checkbox - only the course instructor, logged
                             in, may toggle this (gated by viva.is_course_instructor). -->
                        <label v-if="viva.is_course_instructor" class="highlight-checkbox" @click.stop>
                          <input
                            type="checkbox"
                            :checked="question.highlighted"
                            @change="toggleQuestionHighlight(question)"
                          />
                        </label>
                      </div>
                    </div>
                    <p v-if="viva.is_course_instructor && studentData?.id" class="highlight-reel-footnote">
                      Checked responses are saved under student's
                      <router-link
                        :to="{ name: 'student-progress', params: { student_id: studentData.id }, query: { course_id: viva.course_id, tab: 'highlights' } }"
                      >Highlight Reel</router-link>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- VivaScore Section (assignment vivas only; most-recent non-redo take).
                 Rendered only once the take's payload has arrived with available: true —
                 feature flag off, population too small, or still loading all show nothing. -->
            <div
              v-if="viva.viva_exam?.viva_type === 'assignment' && availableScore(viva)"
              class="vivascore-section"
            >
              <template v-if="scoredTake(viva)">
                <!-- Full scorecard -->
                <div class="vivascore-card">
                  <!-- Header: VivaScore z-value with tooltip -->
                  <div class="vivascore-header">
                    <VivaTooltip :text="VIVASCORE_TOOLTIP">
                      <span class="vivascore-label">VivaScore</span>
                    </VivaTooltip>
                    <span class="vivascore-value">
                      {{ vivaScores[scoredTake(viva).id].vivascore_z !== null
                          ? vivaScores[scoredTake(viva).id].vivascore_z.toFixed(2)
                          : '—' }}
                    </span>
                    <span class="vivascore-population">
                      (n={{ vivaScores[scoredTake(viva).id].population_size }})
                    </span>
                  </div>

                  <!-- MetricBar grid, one row per utils/vivascore.js METRICS entry -->
                  <div class="vivascore-metrics-grid">
                    <div
                      v-for="metric in METRICS"
                      :key="metric.key"
                      class="vivascore-metric-row"
                    >
                      <VivaTooltip :text="metric.tooltip">
                        <span class="metric-label">{{ metric.label }}</span>
                      </VivaTooltip>
                      <MetricBar
                        :dots="[{ z: vivaScores[scoredTake(viva).id].metrics[metric.key]?.z ?? null }]"
                      />
                    </div>
                  </div>

                  <!-- Violin accordion -->
                  <div class="vivascore-violin-toggle">
                    <a
                      href="#"
                      @click.prevent="$set(showViolin, scoredTake(viva).id, !showViolin[scoredTake(viva).id])"
                      class="vivascore-accordion-link"
                    >
                      {{ showViolin[scoredTake(viva).id] ? '▲ Hide class violin' : '▼ View class violin' }}
                    </a>
                    <div v-if="showViolin[scoredTake(viva).id]" class="vivascore-violin">
                      <VivaViolinChart
                        :metrics="violinData(vivaScores[scoredTake(viva).id])"
                      />
                    </div>
                  </div>
                </div>
              </template>
            </div>

            <!-- Rating Section for Admissions VIVAs (inside each tab).
                 hideRatingInterface: despite the name, true means SHOW - see the comment on
                 VivaSettings.hide_rating_interface in shield/viva/models.py. -->
            <div v-if="viva.viva_exam?.viva_type === 'admissions' && hideRatingInterface && viva.is_course_instructor" class="rating-interface">
              <div class="rating-layout">
                <!-- Rating Input State -->
                <div v-if="viva.ratingState === 'input'" class="rating-container">
                  <div class="rating-header">
                    <h3 class="rating-title">
                      Please move the
                      <span class="viva-text-highlight">orange bar</span> to rate this
                      VIVA.
                    </h3>
                  </div>

                  <div class="rating-content">
                    <!-- Left Column: Star Rating Component -->
                    <div class="star-rating-column">
                      <div class="star-rating-section">
                        <!-- Star Column -->
                        <div class="stars-column">
                          <div
                            v-for="star in 5"
                            :key="star"
                            class="star-item"
                            :class="{
                              filled: star <= Math.floor(viva.currentRating),
                              'half-filled':
                                star === Math.ceil(viva.currentRating) &&
                                viva.currentRating % 1 === 0.5,
                            }"
                          >
                            <i class="fa fa-star"></i>
                          </div>
                        </div>

                        <!-- Orange Slider Bar -->
                        <div class="slider-container" @click="updateRating">
                          <div class="slider-track"></div>
                          <div
                            class="slider-bar"
                            :style="{ height: (viva.currentRating / 5) * 100 + '%' }"
                          ></div>
                          <div
                            class="slider-handle"
                            :style="{
                              bottom: `calc(${(viva.currentRating / 5) * 100}% - 5px)`,
                            }"
                            @mousedown="startDrag"
                          ></div>
                        </div>
                      </div>
                    </div>
                    <!-- Right Column: Comments -->
                    <div class="comments-column">
                      <textarea
                        v-model="viva.reviewComments"
                        class="comments-textarea"
                        rows="4"
                        placeholder="Optional review comments"
                      ></textarea>
                    </div>
                  </div>

                  <!-- Rating Display and Rubric Button (horizontal row) -->
                  <div class="rating-controls-row">
                    <div class="current-rating">
                      <span class="rating-number">{{
                        viva.currentRating.toFixed(1)
                      }}</span>
                      <span class="rating-label">My Rating</span>
                    </div>

                    <button
                      class="viva-btn viva-btn-orange rubric-button"
                      @click="showRubricModal = true"
                    >
                      <i class="fa fa-lightbulb-o rubric-lightbulb"></i>
                      SUGGESTED RUBRIC
                    </button>
                  </div>

                  <!-- Bottom Section -->
                  <div class="rating-bottom">
                    <div class="info-text">
                      After your rating is submitted, you will be able to see ratings
                      that may have been submitted by others.
                    </div>
                    <!-- Name Input (appears when rating is set) -->
                    <div v-if="viva.currentRating >= 0" class="name-section">
                      <input
                        v-model="reviewerName"
                        type="text"
                        class="form-input name-input"
                        placeholder="Your Name (Example: 'Professor Smith')"
                      />
                    </div>

                    <div class="rating-actions">
                      <button
                        class="viva-btn viva-btn-gray submit-rating-button"
                        @click="submitRating"
                        :disabled="!viva.hasInteractedWithSlider || viva.submittingRating"
                      >
                        <span v-if="viva.submittingRating">Submitting...</span>
                        <span v-else>ENTER RATING</span>
                      </button>

                      <a href="#" class="no-rating-link" @click.prevent="skipRating">
                        No Rating At This Time
                      </a>
                    </div>
                  </div>
                </div>

                <!-- All Ratings View -->
                <div v-if="viva.ratingState === 'view'" class="ratings-view-container">
                  <div class="ratings-header">
                    <h3>ALL RATINGS</h3>
                  </div>

                  <div class="ratings-list">
                    <!-- No ratings message -->
                    <div v-if="viva.allRatings.length === 0" class="no-ratings-message">
                      <p>No ratings for this VIVA at this time.</p>
                    </div>

                    <!-- Ratings list -->
                    <div
                      v-for="rating in viva.allRatings"
                      :key="rating.id"
                      class="rating-item"
                    >
                      <div class="rating-display">
                        <span class="rating-display-number">{{
                          rating.rating.toFixed(1)
                        }}</span>
                      </div>
                      <span class="rating-text"
                        >{{ rating.reviewerName }} gave this VIVA
                        {{ rating.rating.toFixed(1) }} stars</span
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Grading Section for Assignments. Deliberately checks is_instructor,
           not is_course_instructor - the latter is tied to the Highlight Reel
           feature flag and shouldn't gate an unrelated feature. -->
      <div v-if="showGradingInterface && currentViva && currentViva.is_instructor" class="rating-interface">
        <div class="rating-layout">
          <div v-if="currentViva.ratingState === 'input'" class="rating-container">
            <div class="rating-header">
              <h3 class="rating-title">Optional grade and comments</h3>
              <p class="rating-subtitle">(for internal use only; not shared with student)</p>
            </div>

            <div class="grading-content">
              <!-- Grade Input Section -->
              <div class="grade-input-wrapper">
                <input
                  v-model="currentViva.currentRating"
                  type="number"
                  min="0"
                  max="100"
                  placeholder="N/A"
                  class="grade-input"
                />
                <span class="grade-suffix">/100</span>
              </div>

              <!-- Comments Section -->
              <div class="comments-section">
                <textarea
                  v-model="currentViva.reviewComments"
                  class="comments-textarea"
                  rows="6"
                  placeholder="Optional comments"
                ></textarea>
              </div>
            </div>

            <div class="rating-actions">
                <button
                  class="viva-btn viva-btn-green submit-rating-button"
                  @click="submitRating"
                  :disabled="currentViva.submittingRating"
                >
                  <span v-if="currentViva.submittingRating">Saving...</span>
                  <span v-else>Save Grade</span>
                </button>
              </div>
          </div>

          <!-- View grades -->
          <div v-if="currentViva.ratingState === 'view'" class="ratings-view-container">
            <div class="ratings-header">
              <h3>Student Grades</h3>
            </div>

            <div class="ratings-list">
              <div
                v-for="rating in currentViva.allRatings"
                :key="rating.id"
                class="rating-item"
              >
                <div class="rating-display">
                  <span class="rating-display-number">{{
                    rating.numericGrade !== null && rating.numericGrade !== undefined ? rating.numericGrade : 'N/A'
                  }}</span>
                </div>
                <span class="rating-text">
                  <span v-if="rating.comments">{{ rating.reviewerName }} said "{{ rating.comments }}"</span>
                  <span v-else>{{ rating.reviewerName }}</span>
                </span>
              </div>
            </div>
            <div class="text-center mt-4">
              <button
                class="viva-btn viva-btn-small viva-btn-orange"
                @click="currentViva.ratingState = 'input'"
                >Add another grade</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Suggested Rubric Modal -->
      <div
        v-if="showRubricModal"
        class="viva-modal-backdrop"
        @click="closeRubricModal"
      >
        <div class="viva-modal-content rubric-modal" @click.stop>
          <div class="viva-modal-header">
            <h2>SUGGESTED RUBRIC FOR RATING</h2>
          </div>
          <div class="viva-modal-body">
            <div class="rubric-items">
              <div
                class="rubric-item"
                v-for="item in rubricItems"
                :key="item.rating"
              >
                <span class="rubric-number">{{ item.rating }}</span>
                <i class="fa fa-star rubric-star"></i>
                <span class="rubric-description">{{ item.description }}</span>
              </div>
            </div>
          </div>
          <div class="viva-modal-footer">
            <button
              class="viva-btn viva-btn-orange rubric-close-button"
              @click="closeRubricModal"
            >
              CLOSE
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Back Link (assignment VIVAs only — admissions VIVAs have no assignment to go back to) -->
    <div
      v-if="currentViva?.viva_exam?.viva_type === 'assignment' && currentViva.viva_exam.assignment_id"
      class="back-section"
    >
      <a
        :href="env.serverAddr + '/viva/instructor/assignment/' + currentViva.viva_exam.assignment_id + '/submissions'"
        target="_blank"
        class="back-link"
        @click="handleBackToAssignment"
      >
        <span>←</span> Back to Assignment
      </a>
    </div>

    <div class="footer-content">
      <p class="text-link">
        <a class="link-accent" :href="env.serverAddr + '/viva/terms-and-conditions/'" target="_blank">
          terms and conditions
        </a>
        and
        <a class="link-accent" :href="env.serverAddr + '/viva/privacy-policy/'" target="_blank">
          privacy policy
        </a>
      </p>
    </div>
  </div>
</template>
<script>
import axios from "axios";
import apiClient from "../utils/apiClient.js";
import Header from "@/components/Header.vue";
import IVPlayer from "@/components/IVPlayer.vue";
import PDFViewer from "@/components/PDFViewer.vue";
import VivaViolinChart from "@/components/VivaViolinChart.vue";
import MetricBar from "@/components/MetricBar.vue";
import VivaTooltip from "@/components/VivaTooltip.vue";
import { METRICS, VIVASCORE_TOOLTIP } from "../utils/vivascore.js";
import env from "../utils/env.js";

export default {
  name: "Review",
  components: { Header, IVPlayer, PDFViewer, VivaViolinChart, MetricBar, VivaTooltip },
  data() {
    return {
      env: env,
      // URL parameters
      uniqueUrlString: null,

      // Student data (from API)
      studentData: null,
      organization: null,
      hideRatingInterface: false,

      // Multi-VIVA structure
      vivas: [],
      activeVivaIndex: 0,
      expandedTakeId: null,

      // UI state
      loading: true,
      error: null,
      showTranscript: false,
      showPDF: false,
      PDFRendered: false,
      downloadingProject: false,
      contentTotalHeight: 950,

      // Rating system data (shared across VIVAs)
      reviewerName: "",
      showRubricModal: false,
      isDragging: false,

      // VideoJS player instance
      videoPlayer: null,

      // VivaScore state — keyed by take.id
      vivaScores: {},
      showViolin: {},

      // VivaScore metadata constants
      METRICS,
      VIVASCORE_TOOLTIP,

      // Rubric items for modal
      rubricItems: [
        {
          rating: 5,
          description:
            "Tremendous responses on par with how a student at our institution would be expected to interact with a professor about a complex topic",
        },
        {
          rating: 4,
          description:
            "Very good responses that show mastery of the project topic and promise as a researcher",
        },
        {
          rating: 3,
          description:
            "Adequate responses that demonstrate sufficient understanding of the topic and the potential to become an effective communicator of complex topics",
        },
        {
          rating: 2,
          description:
            "Weak responses that suggest areas where understanding is incomplete",
        },
        {
          rating: 1,
          description:
            "Poor responses where the lack of understanding of the project topic is easily apparent",
        },
      ],
      resizeObserver: null,
    };
  },

  computed: {
    // Current active VIVA
    currentViva() {
      return this.vivas[this.activeVivaIndex] || null;
    },
    // Current expanded take
    currentTake() {
      if (!this.currentViva || !this.expandedTakeId) return null;
      return this.currentViva.takes.find(t => t.id === this.expandedTakeId);
    },
    // Current project URL
    currentProjectUrl() {
      if (!this.currentViva || !this.currentViva.paper_uid) return null;
      // Note: The staticAddr was causing CORS error issues on production
      return `${env.serverAddr}/video/v1/file/${this.currentViva.paper_uid}/`;
    },
    // Current send ID (for ratings)
    currentSendId() {
      return this.currentViva?.send_id || null;
    },
    // Only a logged-in instructor of this VIVA's course may link to the Student
    // Progress / Highlight Reel page — everyone else holding this public link
    // (a parent, the student) sees plain text instead.
    canLinkToStudentProgress() {
      return !!(this.currentViva?.is_course_instructor && this.studentData?.id);
    },
    // Show numeric grade input (0-100) for assignment VIVAs
    showGradingInterface() {
      return this.currentViva?.viva_exam?.viva_type === 'assignment';
    },
    // Show star rating interface for admissions VIVAs (only if org opted in).
    // Currently unused (the per-tab rating block at line ~351 checks the loop's own `viva`,
    // not `currentViva`) - kept correct for whichever future caller needs the "current tab"
    // version. hideRatingInterface: despite the name, true means SHOW - see the comment on
    // VivaSettings.hide_rating_interface in shield/viva/models.py.
    showRatingInterface() {
      return this.currentViva?.viva_exam?.viva_type === 'admissions' && this.hideRatingInterface;
    },
    // Deprecated: previously gated on an alpha flag; now always returns true (Card 709).
    // Can be removed and replaced with a literal `true` at call sites if desired in the future.
    showTranscriptions() {
      return true;
    }
  },

  watch: {
    // Pause video when accordion is closed
    expandedTakeId(newTakeId, oldTakeId) {
      // If an accordion was open and is now closed or switched
      if (oldTakeId && oldTakeId !== newTakeId) {
        this.pauseHiddenVideos();
      }
    },

    // Pause videos when switching VIVA tabs
    activeVivaIndex(newIndex, oldIndex) {
      if (oldIndex !== undefined && newIndex !== oldIndex) {
        this.pauseHiddenVideos();
        this.$nextTick(() => {
          this.setupResizeObserver();
        });
      }
    }
  },

  async mounted() {
    this.uniqueUrlString = this.$route.query.v;

    if (!this.uniqueUrlString) {
      this.error = "Invalid review URL - missing unique identifier";
      this.loading = false;
      return;
    }

    await this.loadStudentData();
    // Load ratings for each VIVA
    for (const viva of this.vivas) {
      await this.loadRatingsForViva(viva);
    }
    this.loadVivaScores();
  },

  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },

  methods: {
    // "Back to Assignment" is normally reached by clicking "review video" on
    // StudentSubmissions.vue, which opens this page via window.open — so that
    // already-authenticated tab is still open behind this one, reachable via
    // window.opener. Closing this tab (instead of navigating, here or in a new tab)
    // returns focus to it instantly: no page load, no re-login. window.opener is only
    // set when this tab was actually opened that way — a bookmarked or emailed Review
    // link has no opener, so this falls through to the normal href navigation (open a
    // new tab, per the anchor's href/target="_blank").
    handleBackToAssignment(event) {
      if (window.opener) {
        event.preventDefault();
        window.close();
      }
    },

    // Returns an Authorization header for the review request, or {} if we have no
    // token. A superadmin reviewing a PENDING send has to be identified as themselves;
    // this tab is opened fresh from Mercury so sessionStorage is empty, and Viva has no
    // app-wide token bootstrap. Scoped to this one request on purpose — nothing else in
    // Viva changes behaviour. Falls back to {} so organisation viewers, who are not
    // logged in, still load the page exactly as before. (card #618)
    async reviewerAuthHeader() {
      let token;
      try {
        token = sessionStorage.apitoken;
      } catch (e) {
        // sessionStorage can throw when site data is blocked; treat as signed out.
        token = undefined;
      }
      if (!token) {
        try {
          const res = await axios.get(
            env.serverAddr + "/api/user/token_from_cookie/",
            { withCredentials: true }
          );
          if (res.data.status === true && res.data.token) {
            token = res.data.token;
          }
        } catch (e) {
          // No valid auth_token cookie — carry on as an unauthenticated viewer.
        }
      }
      return token ? { Authorization: `Token ${token}` } : {};
    },
    async loadStudentData() {
      this.loading = true;
      this.error = null;

      try {
        // Superadmin elevation (seeing PENDING sends) comes from our own API token
        // now, not from a nonce in the URL. (card #618)
        const response = await apiClient.get(
          `/api/viva/review-student/?v=${this.uniqueUrlString}`,
          { headers: await this.reviewerAuthHeader() }
        );

        if (response.data.status !== 200) {
          this.error = response.data.error || "Failed to load review data";
          return;
        }

        // Set data from API response
        this.studentData = response.data.student;
        this.organization = response.data.organization;
        this.hideRatingInterface = response.data.hide_rating_interface || false;

        // Initialize rating state for each VIVA (using Vue.set for reactivity)
        const vivasWithRatings = (response.data.vivas || []).map(viva => {
          return {
            ...viva,
            ratingState: "input",
            // Assignment grade input starts blank so an untouched field submits as
            // "no grade" rather than a real 0 — admissions vivas keep a numeric 0
            // since the star-rating slider math (.toFixed(), division) runs on this
            // same field and requires a number.
            currentRating: viva.viva_exam?.viva_type === 'assignment' ? '' : 0,
            hasInteractedWithSlider: false,
            reviewComments: "",
            submittingRating: false,
            allRatings: [],
            loadingRatings: false
          };
        });

        this.vivas = vivasWithRatings;

        // Show the VIVA that matches the URL (backend provides the correct index)
        this.activeVivaIndex = response.data.initial_viva_index || 0;

        // Expand the most recent take of the active VIVA
        if (this.currentViva && this.currentViva.takes.length > 0) {
          this.expandedTakeId = this.currentViva.takes[0].id;
        }
      } catch (error) {
        console.error("Error loading student data:", error);
        this.error =
          error.response?.data?.error || "Failed to load review data";
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          this.setupResizeObserver();
        });
      }
    },

    scoredTake(viva) {
      return viva.takes ? viva.takes.find(t => t.redo_reason === '') : null;
    },

    // The scored take's VivaScore payload once it has arrived with available: true, else null.
    // Gates the whole VivaScore section: feature flag off, population below the floor, or a
    // still-loading request all render nothing.
    availableScore(viva) {
      const take = this.scoredTake(viva);
      const payload = take ? this.vivaScores[take.id] : null;
      return payload && payload.available ? payload : null;
    },

    // Violin data for a take's VivaScore payload: the composite VivaScore violin
    // first, then the 4 base metrics — same order as the Student Progress page.
    violinData(payload) {
      return [
        {
          label: 'VivaScore',
          studentZ: payload.vivascore_z,
          peers: payload.vivascore_peers_z ?? [],
        },
        ...METRICS.map(m => ({
          label: m.label,
          studentZ: payload.metrics[m.key]?.z ?? null,
          peers: payload.metrics[m.key]?.peers_z ?? [],
        })),
      ];
    },

    async loadVivaScores() {
      for (const viva of this.vivas) {
        // Only score the most-recent non-redo take (redo_reason === '')
        const scoredTake = viva.takes.find(t => t.redo_reason === '');
        if (!scoredTake) continue;

        try {
          const res = await apiClient.get(
            `/api/viva/take-vivascore/?take_id=${scoredTake.id}&v=${this.uniqueUrlString}`
          );
          if (res.data.status === 200) {
            this.$set(this.vivaScores, scoredTake.id, res.data);
          }
        } catch (e) {
          console.error("Error loading VivaScore for take", scoredTake.id, e);
        }
      }
    },

    // Toggle a question's Highlight Reel membership. Optimistic: flips the checkbox
    // immediately, then rolls back if the server rejects it (e.g. the instructor's
    // session expired between page load and the click).
    async toggleQuestionHighlight(question) {
      const previousValue = question.highlighted;
      const newValue = !previousValue;
      this.$set(question, 'highlighted', newValue);

      try {
        const response = await apiClient.post(
          `/api/viva/instructor/question/${question.id}/highlight/`,
          { highlighted: newValue },
          { headers: await this.reviewerAuthHeader() }
        );
        this.$set(question, 'highlighted', response.data.highlighted);
      } catch (e) {
        console.error("Error toggling highlight for question", question.id, e);
        this.$set(question, 'highlighted', previousValue);
        alert("Couldn't update the Highlight Reel. Please try again.");
      }
    },

    // Get video URL for a specific take
    getVideoUrl(take) {
      if (!take || !take.uid) return null;
      return `${env.staticAddr}/player/video/v1/file/${take.uid}/inline`;
    },

    // Get formatted questions for a specific take
    getQuestionsForTake(take) {
      if (!take || !take.questions) return [];
      return take.questions.map((q, index) => ({
        index: index + 1,
        time: this.parseTimestampToSeconds(q.timestamp),
        text: q.question || `QUESTION ${index + 1}`,
        class: 'vjs-marker-orange'
      }));
    },

    // Switch between VIVA tabs
    switchViva(index) {
      this.activeVivaIndex = index;
      // Expand the most recent take of the new VIVA
      if (this.currentViva && this.currentViva.takes.length > 0) {
        this.expandedTakeId = this.currentViva.takes[0].id;
      } else {
        this.expandedTakeId = null;
      }
      this.showTranscript = false;
      this.showPDF = false;
      // Note: No need to reload ratings - they're already loaded for all VIVAs
    },

    // Toggle accordion for takes
    toggleTake(takeId) {
      if (this.expandedTakeId === takeId) {
        // Already expanded, collapse it
        this.expandedTakeId = null;
        this.showPDF = false;
      } else {
        // Expand this take
        this.expandedTakeId = takeId;
      }
      this.showTranscript = false;
    },

    // Format date for display
    formatDate(isoString) {
      if (!isoString) return 'Unknown date';
      const date = new Date(isoString);
      return date.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit'
      });
    },


    async downloadStudentProject() {
      if (this.downloadingProject) return; // Prevent multiple clicks

      try {
        if (!this.currentProjectUrl) {
          alert("Student project not available for download");
          return;
        }

        this.downloadingProject = true;

        // Create download link using the project URL from the current VIVA
        const link = document.createElement("a");
        link.href = this.currentProjectUrl;
        link.download = `student-project-${this.studentData?.name || this.uniqueUrlString}.${this.currentViva.upload_type}`;
        link.target = "_blank"; // Open in new tab as fallback
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Keep spinner visible for a moment to give feedback
        setTimeout(() => {
          this.downloadingProject = false;
        }, 1500);
      } catch (error) {
        console.error("Error downloading student project:", error);
        alert("Failed to download student project. Please try again.");
        this.downloadingProject = false;
      }
    },

    // Rating system methods
    updateRating(event) {
      if (!this.currentViva) return;
      const container = event.currentTarget;
      const rect = container.getBoundingClientRect();
      const y = event.clientY - rect.top;
      const height = rect.height;
      const percentage = Math.max(0, Math.min(1, 1 - y / height));
      this.currentViva.currentRating = Math.round(percentage * 5 * 2) / 2; // Round to nearest 0.5
      this.currentViva.hasInteractedWithSlider = true;
    },

    startDrag(event) {
      this.isDragging = true;
      document.addEventListener("mousemove", this.handleDrag);
      document.addEventListener("mouseup", this.stopDrag);
      event.preventDefault();
    },

    handleDrag(event) {
      if (!this.isDragging || !this.currentViva) return;

      // Find all slider containers and use the one that's visible (in the active VIVA tab)
      const containers = document.querySelectorAll(".slider-container");
      let container = null;

      // Find the visible slider (the one with offsetParent !== null)
      for (const c of containers) {
        if (c.offsetParent !== null) {
          container = c;
          break;
        }
      }

      if (!container) return;

      const rect = container.getBoundingClientRect();
      const y = event.clientY - rect.top;
      const height = rect.height;
      const percentage = Math.max(0, Math.min(1, 1 - y / height));
      this.currentViva.currentRating = Math.round(percentage * 5 * 2) / 2;
      this.currentViva.hasInteractedWithSlider = true;
    },

    stopDrag() {
      this.isDragging = false;
      document.removeEventListener("mousemove", this.handleDrag);
      document.removeEventListener("mouseup", this.stopDrag);
    },

    closeRubricModal() {
      this.showRubricModal = false;
    },

    async submitRating() {
      if (!this.currentViva || !this.currentSendId) return;

      this.currentViva.submittingRating = true;

      try {
        const response = await apiClient.post("/api/viva/submit-rating/", {
          send_id: this.currentSendId,
          rating: this.currentViva.currentRating,
          reviewer_name: this.reviewerName,
          comments: this.currentViva.reviewComments,
          isAssignment: this.showGradingInterface
        });

        if (response.data.status !== 200) {
          throw new Error(response.data.error || "Failed to submit rating");
        }


        // Reload ratings for this VIVA to show the new one
        await this.loadRatingsForViva(this.currentViva);

        // Switch to view state
        this.currentViva.ratingState = "view";
      } catch (error) {
        console.error("Error submitting rating:", error);
        alert(
          error.response?.data?.error ||
            "Failed to submit rating. Please try again."
        );
      } finally {
        this.currentViva.submittingRating = false;
      }
    },

    async skipRating() {
      // Load ratings and switch to view state without submitting a rating
      if (this.currentViva) {
        await this.loadRatingsForViva(this.currentViva);
        this.currentViva.ratingState = "view";
      }
    },

    async loadRatingsForViva(viva) {
      if (!viva || !viva.send_id) return;

      viva.loadingRatings = true;

      try {
        const response = await apiClient.get(
          `/api/viva/ratings/?send_id=${viva.send_id}`
        );

        if (response.data.status === 200) {
          // Map ratings to the format expected by the UI
          let mappedRatings = response.data.ratings.map((rating) => ({
            id: rating.id,
            rating: rating.rating,
            numericGrade: rating.numeric_grade,
            reviewerName: rating.reviewer_name,
            comments: rating.comments,
            reviewerInstitution: rating.reviewer_institution,
            createdDate: rating.created_date,
          }));

          // Determine if this is an assignment or admissions VIVA
          const isAssignment = viva.viva_exam?.viva_type === 'assignment';

          // Only show ratings appropriate for the viva type. Assignment ratings can be
          // comment-only (no grade), so keep those too rather than just ones with a grade.
          viva.allRatings = mappedRatings.filter(r =>
            isAssignment ? (r.numericGrade !== null || r.comments) : r.rating !== null
          );

          if (viva.allRatings.length > 0 && isAssignment) {
            viva.ratingState = "view";
          }

        } else {
          console.error("Failed to load ratings:", response.data.error);
        }
      } catch (error) {
        console.error("Error loading ratings:", error);
        // Don't show alert for ratings load error - it's not critical
      } finally {
        viva.loadingRatings = false;
      }
    },

    seekToTimestamp(timestamp) {
      // playbackVideo is now an array of players (one per take)
      // Find the visible/active player
      const players = this.$refs.playbackVideo;

      if (!players) return;

      // If it's a single player, use it directly
      if (!Array.isArray(players)) {
        players.seekToTimestamp(timestamp);
        return;
      }

      // If it's an array, find the visible one and seek
      // The visible player is the one for the currently expanded take
      for (const player of players) {
        if (player && player.$el && player.$el.offsetParent !== null) {
          // This player is visible (not hidden by v-show)
          player.seekToTimestamp(timestamp);
          break;
        }
      }
    },

    handlePlayerReady(player) {
      this.videoPlayer = player;
    },

    parseTimestampToSeconds(timestamp) {
      if (!timestamp) return 0;

      const timeParts = timestamp.toString().split(":");
      let seconds = 0;

      if (timeParts.length === 2) {
        // MM:SS format
        const minutes = parseInt(timeParts[0], 10) || 0;
        const secs = parseInt(timeParts[1], 10) || 0;
        seconds = minutes * 60 + secs;
      } else if (timeParts.length === 3) {
        // HH:MM:SS format
        const hours = parseInt(timeParts[0], 10) || 0;
        const minutes = parseInt(timeParts[1], 10) || 0;
        const secs = parseInt(timeParts[2], 10) || 0;
        seconds = hours * 3600 + minutes * 60 + secs;
      } else if (timeParts.length === 1) {
        // SS format
        seconds = parseInt(timeParts[0], 10) || 0;
      }

      return seconds;
    },

    pauseHiddenVideos() {
      // Wait a tick to ensure DOM is updated
      this.$nextTick(() => {
        const players = this.$refs.playbackVideo;

        if (!players) return;

        // Handle both single player and array of players
        const playerArray = Array.isArray(players) ? players : [players];

        // Pause all non-visible players
        playerArray.forEach(player => {
          if (player && player.$el && player.$el.offsetParent === null) {
            // This player is hidden (v-show=false), pause it
            const videoJsPlayer = player.myPlayer;
            if (videoJsPlayer && typeof videoJsPlayer.pause === 'function') {
              videoJsPlayer.pause();
            }
          }
        });
      });
    },

    // PDF Viewer
    togglePDFViewer() {
      if (!this.PDFRendered) {
        this.PDFRendered = true;
      }
      this.showPDF = !this.showPDF;
    },

    setupResizeObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      this.resizeObserver = new ResizeObserver((entries) => {
        const activeEl =
          this.$refs.projectContent &&
          this.$refs.projectContent[this.activeVivaIndex];

        if (!activeEl) return;

        for (const entry of entries) {
          if (entry.target === activeEl) {
            this.contentTotalHeight = entry.contentRect.height;
          }
        }
      });

      const elements = this.$refs.projectContent;
      if (Array.isArray(elements)) {
        elements.forEach((el) => this.resizeObserver.observe(el));
      } else if (elements) {
        this.resizeObserver.observe(elements);
      }
    },
  },
};
</script>

<style scoped>
@import "../styles/viva-classes.css";

.reviewer-page {
  background-color: var(--viva-cream);
  min-height: 100vh;
  padding: 0;
  position: relative;
}

/* Header is provided by `Header.vue` component; header styles live there. */

/* Loading and Error States (only styles not in viva-classes.css) */
.text-center {
  text-align: center;
}


.error-actions {
  margin-top: 15px;
}

/* Main Container */
.main-container {
  max-width: 1200px;
  margin: 20px auto;
  padding: 0 50px;
  width: 100%;
}

/* Main Layout */
.reviewer-interface {
  padding: 20px 0;
}

.reviewer-layout {
  display: flex;
  gap: var(--panel-gap);
  align-items: flex-start;
  width: 100%;
  justify-content: space-between;
}

.single-take-layout {
  flex-basis: 100%;
}

.panel-width {
  width: calc(50% - calc(var(--panel-gap) * 0.5));
}

/* Student Name Header */
.student-name-header {
  text-align: left;
  margin-bottom: 20px;
}

.student-name-header h1 {
  color: var(--viva-dark);
  font-size: 24px;
  font-weight: 575;
  margin: 0;
  text-transform: none;
  letter-spacing: 0.5px;
  display: inline-block;
  padding: 8px 20px;
  background: rgba(245, 176, 65, 0.1);
  border-left: 4px solid var(--viva-orange);
  border-radius: 4px;
  margin-bottom: 5px;
}

/* VIVA Tabs */
/* VIVA Content Wrapper - contains tabs and content */
.viva-content-wrapper {
  position: relative;
}

.viva-tabs {
  display: flex;
  gap: 8px;  /* Space between tabs */
  margin-bottom: -2px;  /* Overlap with content border */
  justify-content: flex-start;
  padding-left: 20px;
  position: relative;
  z-index: 1;
}

.viva-tab {
  padding: 10px 40px;
  border: 2px solid var(--viva-green);
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  background: rgba(128, 194, 151, 0.15);  /* Light green tint for inactive */
  color: var(--viva-dark);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.viva-tab:hover {
  background: rgba(128, 194, 151, 0.25);
}

.viva-tab.active {
  background: var(--viva-cream);
  border-color: var(--viva-green);
  color: var(--viva-dark);
  position: relative;
  z-index: 2;
}

/* VIVA Content */
.viva-content {
  background: var(--viva-cream);
}

/* Only show border, padding, and margin when there are multiple VIVAs with tabs */
.viva-content.with-tabs {
  border: 2px solid var(--viva-green);
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 30px;
}

/* Project Title */
.project-title {
  text-align: center;
}

.project-title h2{
  margin-top: 0;
  margin-bottom: 15px;
}

/* Project Content */
.project-content {
  --panel-gap: 30px;
  display: flex;
  flex-direction: row;
  gap: var(--panel-gap);
}

/* Takes Accordion */
.takes-accordion {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.take-item {
  border: 2px solid var(--viva-green);
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.take-item.expanded {
  border-color: var(--viva-orange);
}

.take-header {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  background: var(--viva-green);
  color: var(--viva-white);
  cursor: pointer;
  transition: background 0.2s ease;
}

.take-header:hover {
  background: #6bb396;
}

.take-item.expanded .take-header {
  background: var(--viva-orange);
}

.take-number {
  font-size: 16px;
  font-weight: 600;
  margin-right: 20px;
}

.take-date {
  font-size: 14px;
  opacity: 0.9;
  flex: 1;
}

.take-chevron {
  font-size: 14px;
  transition: transform 0.2s ease;
}

.take-content {
  padding: 25px;
  background: var(--viva-cream);
}

/* Left Column - Student Information */
.student-panel {
  flex: 0 0 50%;
  width: 100%;
}

.student-card {
  /* Remove green background so header and video align visually */
  background: transparent;
  border-radius: 6px;
  padding: 0; /* let header and video define their own spacing */
  height: fit-content;
  overflow: visible; /* allow header rounding to be visible */
}

.student-header {
  background: var(--viva-orange);
  color: var(--viva-white);
  padding: 3px 16px; /* slightly reduced vertical padding for a thinner header */
  text-align: center;
  border-radius: 6px;
  margin: 0 0 8px 0; /* add small gap below header so it doesn't touch video */
  display: block;
  z-index: 1;
}

.student-label {
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 1.5px;
}

/* Video Container */
.video-container {
  position: relative;
  background: var(--viva-white);
  border-radius: 12px;
  border: 3px solid var(--viva-green);
  overflow: hidden;
  aspect-ratio: 16/10;
  margin: 0; /* remove extra margins so it aligns with header */
  width: 100%;
}

.no-video-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  color: var(--viva-grey);
  text-align: center;
}

.no-video-placeholder i {
  font-size: 48px;
  margin-bottom: 15px;
  color: var(--viva-green);
}

.video-controls-overlay {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
}

.blob-load-button {
  background: var(--viva-orange);
  color: var(--viva-white);
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background-color 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.blob-load-button:hover {
  background: #e6a84f;
}

.blob-load-button i {
  font-size: 11px;
}

.review-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Hide seeking bar when browser doesn't support proper seeking (before blob is available) */
.review-video.no-seek-support::-webkit-media-controls-timeline {
  display: none;
}

.review-video.no-seek-support::-webkit-media-controls-current-time-display {
  display: none;
}

.review-video.no-seek-support::-webkit-media-controls-time-remaining-display {
  display: none;
}

/* Right Column - Questions */
.questions-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.questions-section {
  display: flex;
  flex-direction: column;
  gap: 25px;
  max-height: 500px;
  overflow-y: auto;
  /* Left padding must clear the suggested-reel-indicator's -56px offset
     (see .suggested-reel-indicator below) - overflow-y: auto forces
     overflow-x to auto too, so anything outside the padding box clips. */
  padding: 10px 10px 10px 60px;
}

.question-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
}

.question-display-wrapper {
  position: relative;
  width: 100%;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.question-display-wrapper:hover {
  transform: translateY(-2px);
}

.highlight-checkbox {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.highlight-checkbox input[type="checkbox"] {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: var(--viva-green);
}

.highlight-reel-footnote {
  margin-top: 12px;
  text-align: right;
  font-size: 1.15rem;
  font-weight: 500;
  color: var(--viva-green, #4caf7d);
}

.highlight-reel-footnote a {
  color: inherit;
  font-size: 1.15rem;
  font-weight: 700;
}

.question-indicator {
  position: absolute;
  top: -8px;
  left: -8px;
  z-index: 10;
}

.question-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: var(--viva-orange);
  color: var(--viva-white);
  border-radius: 50%;
  font-size: 14px;
  font-weight: 600;
}

.question-display {
  width: 100%;
  border: 2px solid var(--viva-green); /* use viva-green variable */
  padding: 15px 18px;
  font-size: 16px;
  font-weight: 500;
  background: var(--viva-white);
  border-radius: 8px;
  color: var(--viva-grey);
  min-height: 50px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.question-display-wrapper:hover .question-display {
  border-color: var(--viva-orange);
  box-shadow: 0 2px 8px rgba(245, 176, 65, 0.2);
}

.question-display--highlighted {
  border-width: 4px;
  border-color: var(--viva-green);
}

.highlight-reel-indicator {
  position: absolute;
  top: -8px;
  right: -8px;
  z-index: 10;
}

.highlight-reel-indicator img {
  display: block;
  width: 28px;
  height: 28px;
}

.suggested-reel-indicator {
  position: absolute;
  top: 50%;
  /* Sits in the gap left of the question card (per the Highlight Reel mockup) -
     -56px clears the 48px-wide stamp image with an 8px gap before the card's
     edge, rather than overlapping it. */
  left: -56px;
  transform: translateY(-50%);
  z-index: 10;
  cursor: default;
}

.suggested-reel-indicator img {
  display: block;
  width: 48px;
  height: 43px;
}

.timestamp-badge {
  background: var(--viva-orange);
  color: var(--viva-white);
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
  margin-left: 10px;
}

/* Paper display */
.paper-panel {
  position: relative;
  width: 50%;
  flex-shrink: 0;
}

.paper-close {
  position: absolute;
  z-index: 10;
  top: -16px;
  left: -16px;
}

.paper-viewer-container {
  width: min(150%, calc(50vw - calc(var(--panel-gap) * 1.5)));
  position: absolute;
  right: 0;
}

.paper-viewer {
  --container-background: #f0f2f5;
  background: var(--container-background);
  border: 2px solid var(--viva-green);
  border-radius: 12px;
  height: calc(100vh - 30px);
  max-height: var(--content-total-height);
  overflow: hidden;
}

.paper-scroll-area {
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
}

/* Transcript Link (unobtrusive) */
.transcript-link-container {
  text-align: center;
  margin-top: 10px;
  padding: 5px 0;
}

.transcript-link {
  color: var(--viva-grey);
  font-size: 13px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: color 0.2s ease;
}

.transcript-link:hover {
  color: var(--viva-orange);
  text-decoration: underline;
}

.transcript-link i {
  font-size: 12px;
}

/* Transcript Display */
.transcript-container {
  margin-top: 15px;
  margin-bottom: 10px;
  overflow: hidden;
  background: var(--viva-white);
  border: 2px solid var(--viva-green);
  border-radius: 8px;
}

.transcript-disclaimer {
  font-size: 12px;
  color: red;
  padding: 6px 15px 0 15px;
}

.transcript-display {
  max-height: 275px;
  overflow-y: auto;
  padding: 15px;
}

.transcript-content {
  font-size: 14px;
  line-height: 1.6;
  color: var(--viva-grey);
  white-space: pre-wrap;
  word-wrap: break-word;
}

/* Bottom Section */
.submit-row {
  display: flex;
  align-items: center;
  gap: 20px;
  width: 100%;
  margin-top: 30px
}

.submit-button {
  font-size: 20px;
  font-weight: 600;
  padding: 0 40px;
  height: 48px;
  flex: 1;
  border-radius: 25px;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.submit-button-small {
  padding: 0px;
  flex-grow: 0;
  min-width: 48px;
}

/* Rating Section */
.rating-interface {
  min-height: auto;
  background-color: var(--viva-cream);
  padding: 20px 0;
  margin: 0 auto;
}

.rating-layout {
  display: flex;
  gap: 60px;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
}


/* Rating Container */
.rating-container {
  background: var(--viva-white);
  border: 2px solid var(--viva-green);
  border-radius: 18px;
  padding: 25px 40px 25px 40px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  width: 100%;
}

.rating-header {
  text-align: center;
  margin-bottom: 25px;
}

.rating-header .rating-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--viva-grey);
  margin: 0;
  letter-spacing: 0.8px;
}

.rating-subtitle {
  font-size: 12px;
  font-weight: 400;
  color: var(--viva-grey);
  margin: 4px 0 0 0;
  letter-spacing: 0.3px;
}

.viva-text-highlight {
  color: var(--viva-orange);
  font-weight: 700;
}

.rating-content {
  display: flex;
  gap: 30px;
  margin-bottom: 25px;
  align-items: flex-start;
}

/* Left Column - Star Rating */
.star-rating-column {
  flex: 0 0 30%;
  display: flex;
  flex-direction: column;
  gap: 15px;
  align-items: center;
}

.star-rating-section {
  display: flex;
  align-items: flex-start;
  gap: 15px;
}

.stars-column {
  display: flex;
  flex-direction: column-reverse;
  gap: 2px;
  margin-right: 15px;
}

.star-item {
  font-size: 30px;
  color: #ddd;
  transition: color 0.2s ease;
}

.star-item.filled {
  color: var(--viva-orange);
}

.star-item.half-filled {
  background: linear-gradient(to top, var(--viva-orange) 53%, #ddd 47%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

/* Slider Components */
.slider-container {
  position: relative;
  width: 6px;
  height: 220px;
  cursor: pointer;
  margin-right: 20px;
  margin-top: 4px;
}

.slider-track {
  position: absolute;
  width: 100%;
  height: 100%;
  background: #e0e0e0;
  border-radius: 3px;
}

.slider-bar {
  position: absolute;
  bottom: 0;
  width: 100%;
  background: var(--viva-orange);
  border-radius: 3px;
  transition: height 0.2s ease;
}

.slider-handle {
  position: absolute;
  width: 35px;
  height: 12px;
  background: var(--viva-orange);
  margin-left: -12px;
  margin-top: -2px;
  cursor: grab;
  transition: bottom 0.2s ease;
}

.slider-handle::after {
  content: "";
  position: absolute;
  left: -8px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 6px solid transparent;
  border-bottom: 6px solid transparent;
  border-right: 8px solid var(--viva-orange);
}

.slider-handle:active {
  cursor: grabbing;
}

/* Rating Controls Row - Horizontal Layout */
.rating-controls-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 40px;
  margin-bottom: 25px;
  padding-left: calc(15% - 55px);
}

/* Current Rating Display */
.current-rating {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 500;
}

.rating-number {
  font-size: 56px;
  font-weight: 600;
  color: var(--viva-orange);
  border: 1px solid var(--viva-green);
  border-radius: 11px;
  padding: 0 16px;
}

.rating-star {
  font-size: 30px;
  color: var(--viva-orange);
  margin: 5px;
}

.rating-label {
  font-size: 22px;
  color: var(--viva-orange);
  letter-spacing: 0.8px;
}

.rubric-button {
  font-size: 14px;
  padding: 8px 16px;
  height: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.rubric-lightbulb {
  font-size: 18px;
  color: var(--viva-white);
}

/* Right Column - Comments */
.comments-column {
  height: 220px;
  flex: 1 1 65%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-right: 5%;
}


.comments-textarea {
  width: 100%;
  height: 100%;
  padding: 15px;
  border: 2px solid var(--viva-orange);
  border-radius: 13px;
  font-size: 15px;
  font-family: inherit;
  resize: none;
  background: var(--viva-white);
  line-height: 1.5;
  color: var(--viva-grey);
}

.comments-textarea:focus {
  outline: none;
  border-color: var(--viva-orange);
  box-shadow: 0 0 5px rgba(245, 176, 65, 0.2);
}

.name-section {
  margin-top: 0;
}

.name-input {
  width: 100%;
  max-width: 550px;
  padding: 15px;
  border: 1px solid var(--viva-green);
  border-radius: 6px;
  font-size: 15px;
  color: var(--viva-grey);
  margin: 0 auto;
  display: block;
  letter-spacing: 1px;
}

/* Rating Bottom Section */
.rating-bottom {
  padding-top: 25px;
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.info-text {
  font-size: 20px;
  color: var(--viva-green);
  text-align: center;
  line-height: 1.5;
  margin: 0 auto;
  max-width: 800px;
  font-family: "Times New Roman", Times, serif;
  letter-spacing: 0.6px;
  font-weight: 600;
}

.rating-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}

.submit-rating-button {
  width: 100%;
  max-width: 260px;
  padding: 12px 15px;
  font-size: 16px;
  font-weight: 600;
  background-color: var(--viva-green);
  color: var(--viva-white);
  border: none;
  border-radius: 40px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin-top: 10px;
}

.submit-rating-button:hover:not(:disabled) {
  background-color: #6bb396;
}

.submit-rating-button:disabled {
  background-color: #c0c0c0;
  opacity: 0.7;
  cursor: not-allowed;
}

.no-rating-link {
  font-size: 16px;
  color: var(--viva-grey);
  cursor: pointer;
  font-weight: 600;
  margin: 15px 0 40px 0;
}

.no-rating-link:hover {
  color: var(--viva-dark);
  text-decoration: underline;
}

/* All Ratings View */
.ratings-view-container {
  background: var(--viva-white);
  border: 2px solid var(--viva-green);
  border-radius: 8px;
  padding: 25px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  width: 100%;
}

.ratings-header h3 {
  font-size: 24px;
  color: var(--viva-green);
  margin: 0 0 20px 0;
  text-align: center;
  font-family: "Times New Roman", Times, serif;
}

.ratings-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 15px;
  margin-left: 5%;
}

.no-ratings-message {
  text-align: center;
  padding: 40px 20px;
  color: var(--viva-grey);
  font-style: italic;
}

.no-ratings-message p {
  font-size: 18px;
  margin: 0;
}

.rating-item {
  display: flex;
  align-items: center;
  gap: 10px;
  line-height: 1.4;
}

.rating-display {
  font-size: 15px;
  font-weight: 600;
  color: var(--viva-orange);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 5px;
}

.rating-display-star {
  font-size: 25px;
  color: var(--viva-orange);
  margin-right: 10px;
}

.rating-display-number {
  font-size: 45px;
  font-weight: 600;
  color: var(--viva-orange);
}

.rating-text {
  font-size: 16px;
  color: var(--viva-grey);
}

/* Rubric Modal */
.rubric-modal {
  max-width: 700px;
  width: 90%;
}

.rubric-items {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.rubric-item {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  line-height: 1.6;
}

.rubric-star {
  font-size: 20px;
  color: var(--viva-orange);
  flex-shrink: 0;
  margin-top: 2px;
}

.rubric-number {
  font-size: 20px;
  font-weight: 600;
  color: var(--viva-orange);
  flex-shrink: 0;
  align-self: flex-start;
  margin-top: -4px;
}

.rubric-description {
  font-size: 16px;
  color: var(--viva-grey);
  line-height: 1.5;
}

.rubric-close-button {
  font-size: 14px;
  padding: 8px 20px;
  height: auto;
}

/* Grading Section for Coursework */
.grading-content {
  display: flex;
  gap: 30px;
  align-items: flex-start;
  padding: 10px 0;
}

.grade-input-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.grade-input {
  width: 90px;
  font-size: 32px;
  font-weight: 600;
  color: var(--viva-orange);
  border: 2px solid var(--viva-green);
  border-radius: 8px;
  padding: 8px 16px;
  text-align: center;
}

.grade-input::-webkit-outer-spin-button,
.grade-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.grade-input:focus {
  outline: none;
  border-color: var(--viva-orange);
  box-shadow: 0 0 5px rgba(245, 176, 65, 0.3);
}

.grade-suffix {
  font-size: 32px;
  font-weight: 600;
  color: var(--viva-orange);
}

.comments-section {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.footer-content {
  margin-bottom: 30px;
}

/* VivaScore Section */
.vivascore-section {
  padding: 20px 0;
  margin: 0 auto;
}

.vivascore-card {
  background: var(--viva-white);
  border: 2px solid var(--viva-orange);
  border-radius: 18px;
  padding: 25px 40px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  width: 100%;
}

.vivascore-header {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 20px;
}

.vivascore-label {
  font-size: 18px;
  font-weight: 600;
  color: var(--viva-dark);
  letter-spacing: 0.5px;
  cursor: default;
  border-bottom: 1px dashed var(--viva-grey);
}

.vivascore-value {
  font-size: 28px;
  font-weight: 700;
  color: var(--viva-orange);
}

.vivascore-population {
  font-size: 13px;
  color: var(--viva-grey);
}

.vivascore-metrics-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 30px;
  margin-bottom: 20px;
}

.vivascore-metric-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.metric-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--viva-grey);
  letter-spacing: 0.5px;
  text-transform: uppercase;
  cursor: default;
  border-bottom: 1px dashed var(--viva-grey);
  width: fit-content;
}

.vivascore-violin-toggle {
  margin-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding-top: 14px;
}

.vivascore-accordion-link {
  font-size: 13px;
  color: var(--viva-orange);
  text-decoration: none;
  font-weight: 500;
}

.vivascore-accordion-link:hover {
  text-decoration: underline;
}

.vivascore-violin {
  margin-top: 16px;
}

</style>
